import { renderDocument, readingMinutesFromDoc, wordCountFromDoc } from '@archetelos/editor';
import { useQueryClient } from '@tanstack/react-query';
import { Link, useBlocker } from '@tanstack/react-router';
import { EditorContent, useEditor, type Editor } from '@tiptap/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { composerExtensions } from './editorExtensions';
import { ImageModal } from './ImageModal';
import {
  DISPLAY,
  TOPICS,
  TRADITIONS,
  slugify,
  validateForSubmit,
  type ComposerState,
} from './types';
import { useArticleComposer } from './useArticleComposer';
import { VersionHistoryModal } from './VersionHistoryModal';

import { useAuth } from '@/lib/auth/useAuth';
import { assetQueries, useAssetMap } from '@/lib/queries/assets';

interface Props {
  initialContentId?: string;
  initial: ComposerState;
  initialStatus?: string;
  // Read-only editor feedback (Spec 03 — CR2). Not part of editable ComposerState;
  // surfaced as a banner when the piece is rejected so the creator can act on it.
  reviewerNotes?: string | null;
}

export function ArticleComposer({
  initialContentId,
  initial,
  initialStatus,
  reviewerNotes,
}: Props) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const composer = useArticleComposer({ initialContentId, initial, initialStatus });
  const { state, patch } = composer;
  const [tab, setTab] = useState<'edit' | 'preview'>('edit');
  const [historyOpen, setHistoryOpen] = useState(false);
  const [confirmSubmit, setConfirmSubmit] = useState(false);
  const [slugTouched, setSlugTouched] = useState(Boolean(initial.slug));

  // Inline-image insertion (Spec 40): a two-tab modal (upload new / reuse from
  // the library). It hands back an assetId; the composer inserts the node.
  // Spec 41: the cover uses the SAME picker — a cover is just an asset now.
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [coverModalOpen, setCoverModalOpen] = useState(false);

  // TipTap owns selection, undo, and the toolbar commands natively — the whole
  // insert-at-cursor problem class is gone. The shared `extensions` are the same
  // schema the renderers use, so the editor can't drift from what publishes.
  const editor = useEditor({
    extensions: composerExtensions,
    content: initial.bodyJson,
    onUpdate: ({ editor: e }) => patch({ bodyJson: e.getJSON() }),
    editorProps: {
      attributes: { class: 'prose max-w-none min-h-[55vh] outline-none' },
    },
  });

  useBlocker({
    shouldBlockFn: () => {
      if (!composer.dirty) return false;
      return !window.confirm('You have unsaved changes. Leave anyway?');
    },
  });

  // Live preview through renderDocument — the same function the marketing site
  // calls, resolved through the same asset map, so the preview cannot lie.
  const assetMap = useAssetMap(state.bodyJson);
  const previewHtml = useMemo(
    () => renderDocument(state.bodyJson, assetMap),
    [state.bodyJson, assetMap],
  );
  const words = wordCountFromDoc(state.bodyJson);
  const minutes = readingMinutesFromDoc(state.bodyJson);
  const isPublished = composer.status === 'published';
  // Rejected pieces revise-and-resubmit in place (same row, keeps version history);
  // the banner clears once the status changes back to pending_review.
  const isRejected = composer.status === 'rejected';

  // ⌘K link — prompt, validated by the Link extension's isAllowedUri (a
  // javascript: href is silently refused). Empty clears the link.
  const promptLink = useCallback(() => {
    if (!editor) return;
    const prev = editor.getAttributes('link').href as string | undefined;
    const url = window.prompt('Link URL (http, https, or mailto):', prev ?? 'https://');
    if (url === null) return;
    if (url.trim() === '') {
      editor.chain().focus().unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url.trim() }).run();
  }, [editor]);

  useEffect(() => {
    if (!editor) return;
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k' && editor.isFocused) {
        e.preventDefault();
        promptLink();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [editor, promptLink]);

  // Cover pick (Spec 41): store the canonical reference; resolve url/alt from the
  // asset for the composer thumbnail + version snapshot. The DB trigger writes
  // the cover_image_url/alt cache — the client never does.
  const onPickCover = (assetId: string) => {
    void (async () => {
      const asset = await queryClient.fetchQuery(assetQueries.byId(assetId));
      if (!asset) {
        toast.error('Could not load that image.');
        return;
      }
      patch({
        coverAssetId: assetId,
        coverImageUrl: asset.publicUrl,
        coverImageAlt: asset.altText,
      });
    })();
  };

  const insertImageNode = (assetId: string) => {
    editor?.chain().focus().insertContent({ type: 'image', attrs: { assetId } }).run();
  };

  const onSubmit = async () => {
    const errs = validateForSubmit(state);
    if (errs.length > 0) {
      toast.error(errs[0]!.message);
      return;
    }
    try {
      await composer.submitForReview();
      setConfirmSubmit(false);
      toast.success('Submitted for review. You can keep editing until an editor reviews it.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Unable to submit');
    }
  };

  const saveLabel =
    composer.saveStatus === 'saving'
      ? 'Saving…'
      : composer.saveStatus === 'error'
        ? 'Save failed — retrying'
        : composer.lastSavedAt
          ? `Saved at ${composer.lastSavedAt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`
          : 'Not yet saved';

  return (
    <div className="mx-auto max-w-7xl">
      {isRejected && (
        <div className="mb-6 rounded-md border border-oxblood/30 bg-oxblood/5 p-4">
          <p className="font-sans text-xs uppercase tracking-[0.12em] text-oxblood">
            Editor&apos;s notes
          </p>
          <p className="mt-2 font-sans text-sm text-ink/80 pretty">
            {reviewerNotes?.trim() ? reviewerNotes : 'Declined — no notes provided.'}
          </p>
          <p className="mt-2 font-sans text-xs text-ink/60">
            Make your revisions and resubmit — this returns the piece to the review queue.
          </p>
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="rounded-sm border border-ink/20 px-2 py-0.5 font-sans text-[0.7rem] uppercase tracking-[0.14em] text-ink/60">
            {composer.status.replace('_', ' ')}
          </span>
          <span className="font-sans text-xs text-ink/50">{saveLabel}</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setHistoryOpen(true)}
            disabled={!composer.contentId}
            className="rounded-md border border-ink/20 px-3 py-1.5 text-sm text-ink hover:border-oxblood disabled:opacity-50"
          >
            Version history
          </button>
          <button
            type="button"
            onClick={() => void composer.manualSave()}
            className="rounded-md border border-ink/20 px-3 py-1.5 text-sm text-ink hover:border-oxblood"
          >
            Save (⌘S)
          </button>
          <button
            type="button"
            onClick={() => setConfirmSubmit(true)}
            className="rounded-md bg-ink px-4 py-1.5 text-sm font-medium text-parchment hover:bg-oxblood"
          >
            {isRejected ? 'Resubmit for review' : 'Submit for review'}
          </button>
        </div>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_320px]">
        <div>
          {/* Edit ⇄ Preview is now a full-width toggle at every breakpoint. The
              editor renders through the same .prose/serif as the published page,
              so it's WYSIWYG-accurate; Preview exists to show resolved images and
              the exact reading column, not to correct the editor's typography. */}
          <div className="mb-3 inline-flex rounded-md border border-ink/15 p-0.5">
            {(['edit', 'preview'] as const).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setTab(t)}
                aria-pressed={tab === t}
                className={`rounded-[5px] px-3 py-1 text-sm ${
                  tab === t ? 'bg-ink/10 text-ink' : 'text-ink/60 hover:text-ink'
                }`}
              >
                {t === 'edit' ? 'Edit' : 'Preview'}
              </button>
            ))}
          </div>

          <div className={tab === 'edit' ? '' : 'hidden'}>
            <Toolbar editor={editor} onLink={promptLink} onImage={() => setImageModalOpen(true)} />
            <div className="rounded-md border border-ink/20 bg-parchment p-6 font-serif text-ink">
              <EditorContent editor={editor} />
            </div>
            <p className="mt-2 font-sans text-xs text-ink/50">
              {words} words · ~{minutes} min read
            </p>
          </div>

          <div className={tab === 'preview' ? '' : 'hidden'}>
            <div
              className="prose font-serif rounded-md border border-ink/10 bg-parchment p-6"
              dangerouslySetInnerHTML={{ __html: previewHtml }}
            />
          </div>
        </div>

        <aside className="space-y-5">
          <div>
            <label className="mb-1 block font-sans text-xs uppercase tracking-[0.12em] text-ink/60">
              Title
            </label>
            <input
              value={state.title}
              onChange={(e) => {
                patch({ title: e.target.value });
                if (!slugTouched) patch({ slug: slugify(e.target.value) });
              }}
              className="w-full rounded-md border border-ink/20 bg-parchment px-3 py-2 font-serif text-lg text-ink focus:border-oxblood focus:outline-none"
            />
          </div>
          <div>
            <label className="mb-1 block font-sans text-xs uppercase tracking-[0.12em] text-ink/60">
              Excerpt ({state.excerpt.length}/280)
            </label>
            <textarea
              value={state.excerpt}
              maxLength={280}
              rows={3}
              onChange={(e) => patch({ excerpt: e.target.value })}
              className="w-full rounded-md border border-ink/20 bg-parchment px-3 py-2 text-sm text-ink focus:border-oxblood focus:outline-none"
            />
          </div>
          <div>
            <label className="mb-1 block font-sans text-xs uppercase tracking-[0.12em] text-ink/60">
              Slug {isPublished && <span className="text-ink/40">(locked — published)</span>}
            </label>
            <input
              value={state.slug}
              disabled={isPublished}
              onChange={(e) => {
                setSlugTouched(true);
                patch({ slug: slugify(e.target.value) });
              }}
              className="w-full rounded-md border border-ink/20 bg-parchment px-3 py-2 text-sm text-ink focus:border-oxblood focus:outline-none disabled:opacity-60"
            />
          </div>
          <div>
            <label className="mb-1 block font-sans text-xs uppercase tracking-[0.12em] text-ink/60">
              Cover image
            </label>
            {state.coverImageUrl && (
              <img
                src={state.coverImageUrl}
                alt={state.coverImageAlt}
                className="mb-2 aspect-[16/10] w-full rounded-md object-cover"
              />
            )}
            <button
              type="button"
              onClick={() => setCoverModalOpen(true)}
              className="rounded-md border border-ink/20 px-3 py-1.5 text-xs text-ink hover:border-oxblood"
            >
              {state.coverAssetId ? 'Replace cover' : 'Choose cover'}
            </button>
            {/* Alt lives on the asset, not the piece — shown read-only, edited in
                the library so a correction propagates to every piece using it. */}
            {state.coverAssetId && (
              <p className="mt-2 font-sans text-xs text-ink/60">
                Alt: {state.coverImageAlt || <span className="text-oxblood">none set</span>} ·{' '}
                <Link to="/creator/assets" className="text-oxblood hover:underline">
                  edit in library
                </Link>
              </p>
            )}
          </div>

          <TagPicker
            label="Traditions"
            options={TRADITIONS as readonly string[]}
            selected={state.traditionTags}
            onToggle={(v) =>
              patch({
                traditionTags: state.traditionTags.includes(v)
                  ? state.traditionTags.filter((t) => t !== v)
                  : [...state.traditionTags, v],
              })
            }
          />
          <TagPicker
            label="Topics"
            options={TOPICS as readonly string[]}
            selected={state.topicTags}
            onToggle={(v) =>
              patch({
                topicTags: state.topicTags.includes(v)
                  ? state.topicTags.filter((t) => t !== v)
                  : [...state.topicTags, v],
              })
            }
          />

          <div>
            <p className="mb-1 font-sans text-xs uppercase tracking-[0.12em] text-ink/60">
              Gate policy
            </p>
            {(
              [
                ['free_14_days', 'Free for 14 days, then subscribers only'],
                ['subscribers_only', 'Subscribers only immediately'],
              ] as const
            ).map(([val, lbl]) => (
              <label key={val} className="mt-1 flex items-center gap-2 text-sm text-ink/80">
                <input
                  type="radio"
                  name="gate"
                  checked={state.gatePolicy === val}
                  onChange={() => patch({ gatePolicy: val })}
                  className="text-oxblood focus:ring-oxblood"
                />
                {lbl}
              </label>
            ))}
          </div>

          <div>
            <p className="mb-1 font-sans text-xs uppercase tracking-[0.12em] text-ink/60">Access</p>
            {(
              [
                ['always_free', 'Always free — full body to everyone'],
                ['metered', 'Metered — counts toward a reader’s free reads'],
                ['members_only', 'Members only — no free-read unlock'],
              ] as const
            ).map(([val, lbl]) => (
              <label key={val} className="mt-1 flex items-center gap-2 text-sm text-ink/80">
                <input
                  type="radio"
                  name="access"
                  checked={state.accessTier === val}
                  onChange={() => patch({ accessTier: val })}
                  className="text-oxblood focus:ring-oxblood"
                />
                {lbl}
              </label>
            ))}
          </div>
        </aside>
      </div>

      <VersionHistoryModal
        contentId={composer.contentId}
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        onRestore={(s) => {
          composer.setState(s);
          editor?.commands.setContent(s.bodyJson);
        }}
      />

      <ImageModal
        open={imageModalOpen}
        onClose={() => setImageModalOpen(false)}
        userId={user?.id}
        onInsert={insertImageNode}
      />

      <ImageModal
        open={coverModalOpen}
        onClose={() => setCoverModalOpen(false)}
        userId={user?.id}
        onInsert={onPickCover}
      />

      {confirmSubmit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-6">
          <div className="w-full max-w-md rounded-lg border border-ink/10 bg-parchment p-6">
            <h2 className="font-serif text-xl text-ink">
              {isRejected ? 'Resubmit for review?' : 'Submit for review?'}
            </h2>
            <p className="mt-3 font-sans text-sm text-ink/70">
              You can continue editing until an editor reviews it.
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setConfirmSubmit(false)}
                className="rounded-md border border-ink/20 px-4 py-2 text-sm text-ink hover:border-oxblood"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void onSubmit()}
                className="rounded-md bg-ink px-4 py-2 text-sm font-medium text-parchment hover:bg-oxblood"
              >
                {isRejected ? 'Resubmit' : 'Submit'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// The toolbar is now native TipTap commands with real active-state affordances —
// something the old markdown toolbar never had.
function Toolbar({
  editor,
  onLink,
  onImage,
}: {
  editor: Editor | null;
  onLink: () => void;
  onImage: () => void;
}) {
  if (!editor) return <div className="mb-2 h-8" />;
  const btn = (label: string, active: boolean, run: () => void) => (
    <button
      key={label}
      type="button"
      onClick={run}
      aria-pressed={active}
      className={`rounded-sm border px-2 py-1 text-xs ${
        active
          ? 'border-oxblood bg-oxblood/10 text-oxblood'
          : 'border-ink/15 text-ink/70 hover:border-oxblood hover:text-oxblood'
      }`}
    >
      {label}
    </button>
  );
  const chain = () => editor.chain().focus();
  return (
    <div className="mb-2 flex flex-wrap gap-1">
      {btn('H2', editor.isActive('heading', { level: 2 }), () =>
        chain().toggleHeading({ level: 2 }).run(),
      )}
      {btn('H3', editor.isActive('heading', { level: 3 }), () =>
        chain().toggleHeading({ level: 3 }).run(),
      )}
      {btn('H4', editor.isActive('heading', { level: 4 }), () =>
        chain().toggleHeading({ level: 4 }).run(),
      )}
      {btn('B', editor.isActive('bold'), () => chain().toggleBold().run())}
      {btn('I', editor.isActive('italic'), () => chain().toggleItalic().run())}
      {btn('❝', editor.isActive('blockquote'), () => chain().toggleBlockquote().run())}
      {btn('• List', editor.isActive('bulletList'), () => chain().toggleBulletList().run())}
      {btn('1. List', editor.isActive('orderedList'), () => chain().toggleOrderedList().run())}
      {btn('Link', editor.isActive('link'), onLink)}
      {btn('Image', false, onImage)}
      {btn('HR', false, () => chain().setHorizontalRule().run())}
      {btn('Pull-quote', editor.isActive('pullQuote'), () =>
        chain().toggleNode('pullQuote', 'paragraph').run(),
      )}
      {btn('Drop-cap', editor.isActive('paragraph', { dropCap: true }), () =>
        chain()
          .updateAttributes('paragraph', {
            dropCap: !editor.isActive('paragraph', { dropCap: true }),
          })
          .run(),
      )}
    </div>
  );
}

function TagPicker({
  label,
  options,
  selected,
  onToggle,
}: {
  label: string;
  options: readonly string[];
  selected: string[];
  onToggle: (v: string) => void;
}) {
  return (
    <div>
      <p className="mb-1 font-sans text-xs uppercase tracking-[0.12em] text-ink/60">{label}</p>
      <div className="flex flex-wrap gap-1.5">
        {options.map((o) => {
          const active = selected.includes(o);
          return (
            <button
              key={o}
              type="button"
              onClick={() => onToggle(o)}
              aria-pressed={active}
              className={`rounded-sm border px-2 py-1 text-xs ${
                active
                  ? 'border-oxblood bg-oxblood/10 text-oxblood'
                  : 'border-ink/15 text-ink/70 hover:border-oxblood'
              }`}
            >
              {DISPLAY[o] ?? o}
            </button>
          );
        })}
      </div>
    </div>
  );
}
